import { Observable, throwError } from 'rxjs';

import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, switchMap, take, finalize, retry } from 'rxjs/operators';
import { isNullOrUndefined } from '@asset-old/util/isNullOrUndefined.util';

import {
  ConfiguracaoRequisicaoOperacao,
  ResultadoCallBack,
} from '../../models/configuracao-requisicao-operacao.model';
import { RequisicaoOp } from '../models/requisicao-op.model';
import { RespostaExecucaoOperacao } from '../models/resposta-execucacao-operacao.model';

import { MensagensService } from '../../../constantes/mensagens.service';
import { GuardiaoService } from '../../autorizacao/services/guardiao.service';
import { AppSettingsService } from '../../configuracao/services/app-settings.service';
import { Link } from '../models/session.model';
import { CharonService, IHttpOptions } from '@quickweb/connect-angular';
import { environment } from '@asset-old/env/environment';
import { SessaoService } from '@asset-old/app/core/charon/services/sessao.service';
import { v4 as uuidv4 } from 'uuid';
@Injectable({
  providedIn: 'root',
})
export class ExecutorOperacaoCharonService {
  links: Link;
  cabecalhos: HttpHeaders;
  // Session management is handled by the library via sessionStorage[charonID].
  // We react to 401/403 errors rather than proactively checking expiration.
  // Track retry attempts to avoid infinite loops on 400 errors.
  private _retryAttempted = false;

  constructor(
    private readonly servicoConfiguracao: AppSettingsService,
    private readonly servicoSessao: SessaoService,
    private readonly guardiao: GuardiaoService,
    private readonly charon: CharonService,
  ) {}
  requisicaoOp: RequisicaoOp;

  /**
   * Método responsável por realizar a interpretação da configuração de uma solicitação de execução de um serviço,
   * preenchendo os parâmetros necessários para que a requisição aconteça, tendo a opção de inserir tratamentos específicos de retorno
   * para cada status encontrado, sendo ele de sucesso ou de erro.
   *
   * @template T
   * @param {ConfiguracaoRequisicaoOperacao} configuracao Objeto de Configuração dos Parâmetros Utilizados no Serviço
   * @param {ResultadoCallBack} [callBackSucesso] Função para o tratamento do retorno para execuções com erro
   * @param {ResultadoCallBack} [callBackErro] Função para o tratamento do retorno para execuções com erro
   * @returns
   * @memberof ExecutorOperacaoCharonService
   */
  chamarOperacao<T>(
    configuracao: ConfiguracaoRequisicaoOperacao,
    callBackSucesso?: ResultadoCallBack,
    callBackErro?: ResultadoCallBack,
    file?: any,
  ): Observable<any> {
    const dadosOp = configuracao.nomeOperacao;

    if (!isNullOrUndefined(configuracao.nomeOperacao)) {
      this.requisicaoOp = new RequisicaoOp();
      this.requisicaoOp.method = configuracao.metodo;
      this.requisicaoOp.op = dadosOp;

      if (!isNullOrUndefined(configuracao.parametrosRota)) {
        configuracao.parametrosRota.forEach((p) => {
          this.requisicaoOp.adicionaParametrosPathRequisicao(p);
        });
      }

      if (!isNullOrUndefined(configuracao.parametrosQuery)) {
        configuracao.parametrosQuery.forEach((q) => {
          if (q.value != null)
            this.requisicaoOp.adicionaParametrosQueryRequisicao(q);
        });
      }

      if (!isNullOrUndefined(configuracao.cabecalhos)) {
        configuracao.cabecalhos.forEach((h) => {
          this.requisicaoOp.adicionaCabecalhoObjetoCorpoRequisicao(h);
        });
      }

      if (!isNullOrUndefined(configuracao.corpoOperacao)) {
        this.requisicaoOp.body = configuracao.corpoOperacao;
      }

      return this.executarOperacao(dadosOp, this.requisicaoOp, file).pipe(
        map((resultado) => {
          const dados = resultado;
          if (!isNullOrUndefined(callBackSucesso)) {
            return callBackSucesso(dados);
          } else {
            return dados;
          }
        }),
        catchError((erro) => {
          if (!isNullOrUndefined(callBackErro)) {
            if (isNullOrUndefined(erro?.messages)) {
              erro = {
                messages: [
                  {
                    mensagem:
                      MensagensService.mensagens.charon.erroExecucaoOperacao,
                    tipo: 'ERROR',
                  },
                ],
              };
            }
            return callBackErro(erro);
          } else if (
            !isNullOrUndefined(erro.messages) ||
            !isNullOrUndefined(erro[0]?.message)
          ) {
            return erro.messages
              ? throwError(() => new Error(erro.messages[0].mensagem))
              : throwError(() => new Error(erro[0].message));
          } else {
            return throwError(
              () =>
                new Error(
                  MensagensService.mensagens.charon.erroExecucaoOperacao,
                ),
            );
          }
        }),
      );
    } else {
      return throwError(
        () => new Error(MensagensService.mensagens.charon.nomeOperacaoInvalida),
      );
    }
  }

  /**
   * Método responsável por executar uma operação de uma determinada API via Caronte
   * e retornar os dados do endpoint executado
   * @param {string} hash_operacao Hash da Op
   * @param {RequisicaoOp} operacao Parâmetros de execução da OP
   * @returns {Observable<any>} Dados da execução da OP
   * @memberof ExecutorOperacaoCharonService
   */
  executarOperacao(
    hash_operacao: string,
    operacao: RequisicaoOp,
    file: any,
  ): Observable<any> {
    if (
      !isNullOrUndefined(hash_operacao) &&
      hash_operacao.length > 0 &&
      !isNullOrUndefined(operacao)
    ) {
      let corpo = operacao as any;
      let relOp: string;

      this.configurarChamada(!!file);
      this.charon.setCryptography(environment.charon.isCryptographed);

      if (file) {
        // Re-inicializa sessão Caronte sem criptografia e id forçado 'asfrnt'
        return this.servicoSessao
          .getCharonSessionWithOptions({ cryptography: false, charonID: 'asfrnt' })
          .pipe(
            take(1),
            switchMap(() => {
              this.charon.setCryptography(false);
              const formDataOperacao = operacao as any;
              corpo = new FormData();
              formDataOperacao.body = JSON.stringify(operacao.body).replace(
                /"/g,
                "'",
              );
              corpo.append('file', file);
              corpo.append('request', JSON.stringify(formDataOperacao));
              relOp =
                this.servicoConfiguracao.environment.charon
                  .action_requisicoes_op_form_data;
              const response = (op: string) => {
                this.cabecalhos = this.cabecalhos.set(
                  'x-itau-correlationID',
                  uuidv4()
                );
                const options: IHttpOptions = {
                  headers: this.cabecalhos,
                  useHref: false,
                  charonID: 'asfrnt',
                  entryPoint: environment.charon.entrypoint,
                };
                return this.charon.post(op, corpo, options).pipe(
                  map((res: any) => this.mapResult(res)),
                  catchError((erro: any) => this.mapError(erro)),
                );
              };
              return this.charonSessionExecution(response, relOp);
            }),
            finalize(() => {
              // Restaura sessão padrão com criptografia conforme ambiente
              this.servicoSessao
                .getCharonSessionWithOptions({
                  cryptography: environment.charon.isCryptographed,
                  charonID: environment.charon.isCryptographed? this.servicoConfiguracao.environment.charon.id: this.servicoConfiguracao.environment.charon.idNoCrypto,
                })
                .pipe(take(1))
                .subscribe();
            }),
          );
      } else {
        //get link on session storage based on relOp
        relOp =
          this.servicoConfiguracao.environment.charon.action_requisicoes_op;
        corpo = JSON.stringify(corpo);
      }
      
      // Reset retry flag before each operation
      this._retryAttempted = false;
      
      // Execução direta; inicialização handled pela lib.
      const response = (op: string) => {
        this.cabecalhos = this.cabecalhos.set(
          'x-itau-correlationID',
          uuidv4(),
        );
        const options: IHttpOptions = {
          headers: this.cabecalhos,
          useHref: false,
          charonID: environment.charon.isCryptographed? this.servicoConfiguracao.environment.charon.id: this.servicoConfiguracao.environment.charon.idNoCrypto,
          entryPoint: environment.charon.isCryptographed? this.servicoConfiguracao.environment.charon.entrypoint: this.servicoConfiguracao.environment.charon.entrypointNoCrypto,
        };
        return this.charon.post(op, corpo, options).pipe(
          map((res: any) => this.mapResult(res)),
          catchError((erro: any) => this.mapError(erro)),
        );
      };
      
      // Execute with retry on Charon session expiration (400 error)
      return this.charonSessionExecution(response, relOp).pipe(
        catchError((erro: any) => {
          // If Charon session expired, reinitialize and retry once
          if (erro._charonSessionExpired) {
            return this.servicoSessao.getCharonSession().pipe(
              take(1),
              switchMap(() => {
                // Reconfigure headers after session refresh
                this.configurarChamada(false);
                return this.charonSessionExecution(response, relOp);
              }),
            );
          }
          return throwError(() => erro);
        }),
      );
    } else {
      return throwError(
        () => new Error(MensagensService.mensagens.charon.parametrosInvalidos),
      );
    }
  }

  configurarChamada(isBinary: boolean) {
    const contentType = 'application/json';
    this.links = this.servicoSessao.obterLinksCharon() || {};
    this.cabecalhos = new HttpHeaders().set(
      'Authorization',
      'Bearer ' + this.servicoSessao.obterTokenJwt(),
    );

    if (!isBinary) {
      this.cabecalhos = this.cabecalhos.set('Content-Type', contentType);
    }

    const sessionIdFromLogin = this.servicoSessao.obterSessionId();
    const sessionIdToUse = sessionIdFromLogin && sessionIdFromLogin.length > 0 ? sessionIdFromLogin : '';
    this.cabecalhos = this.cabecalhos.set('Session-Id', sessionIdToUse);
  }

  mapResult(res) {
    const resultado: RespostaExecucaoOperacao = res.body;

    if (
      resultado.statusCode === 200 ||
      resultado.statusCode === 201 ||
      resultado.statusCode === 204 ||
      resultado.statusCode === 202
    ) {
      this.servicoSessao.refreshJWT(res.headers.get('refreshedtoken'));
      if (!isNullOrUndefined(resultado.data)) {
        return resultado.data.data || resultado.data;
      } else {
        return { data: {} };
      }
    } else if (
      resultado.statusCode === 403 ||
      resultado.statusCode === 401 ||
      res.status === 404
    ) {     
      this.servicoSessao.excluirSessao();
      this.guardiao.enviarParaLogin(false);
    } else if (!isNullOrUndefined(resultado.data)) {
      throw resultado.data;
    } else {
      throw MensagensService.mensagens.charon.erroExecucaoOperacao;
    }
  }

  mapError(erro) {
    if (erro.status === 401 || erro.status === 403 || this.isExpiredSessionError(erro.message))  
    {
      this.servicoSessao.excluirSessao();
      this.guardiao.enviarParaLogin(false);
    }
    // Handle 400 errors which typically indicate Charon session expiration
    // Only retry if no error body (pure Charon transport error) and not already retried
    if (erro.status === 400 && !erro.error && !this._retryAttempted) {   
      this._retryAttempted = true;
      // Mark error as retryable so the caller can reinitialize and retry
      const retryableError = { ...erro, _charonSessionExpired: true };
      return throwError(() => retryableError);
    }
    if (erro.error) return throwError(() => erro.error);
    else return throwError(() => erro);
  }

  charonSessionExecution(response, relOp): any {
    return response(relOp);
  }

  isExpiredSessionError(errorMessage: string): boolean {  
    return errorMessage 
          && errorMessage.includes('Unexpected value of the `normalizedNames` header provided. Expecting either a string, a number or an array, but got');
  }
}
